import {ObjectOf} from 'src/types/store';
import {AppDeclaration} from 'src/apps/types';
import {EducationPhase, UserIdentifier, UserType} from 'src/types/enums';
import {lazy} from 'react';
import {hsReducers} from 'src/views/HighSchool/reducers';
import {transferReducers} from 'src/views/Transfer/reducers';
import {adminReducers} from 'src/views/Admin/reducers';
import {undergraduateReducers} from 'undergraduate-student/reducers';
import onboardingReducers from 'shared/student/registration/reducers';
import {parentReducers} from 'src/views/Parent/reducers';
import {reducers as collegePortalReducers} from 'src/redux/college-portal/reducers';
import {collegePortalInitialState} from 'src/redux/studentSearch/initialState';
import {cpModals} from 'src/views/college-portal/modals';
import {districtAdminReducers} from 'district-admin/reducers';
import {educatorReducers} from '../../educator/app/reducers/root-reducer';

const educatorInitialState = {
  collegeProfile: {
    userInfo: {
      user: UserType.EDUCATOR,
    },
    tab: 0,
    loading: true,
    modalPending: {},
  },
};

export const apps: ObjectOf<AppDeclaration> = {
  [UserIdentifier.HS_STUDENT]: {
    app: lazy(() => import('src/views/HighSchool')),
    mobileApp: lazy(() => import('src/views/HighSchool/high-school-mobile-application')),
    reducers: hsReducers,
    initialState: {
      collegeProfile: {
        userInfo: {
          user: EducationPhase.HIGH_SCHOOL,
        },
        tab: 0,
        loading: true,
        modalPending: {},
      },
    },
  },
  [UserIdentifier.CC_STUDENT]: {
    app: lazy(() => import('src/views/Transfer')),
    mobileApp: lazy(() => import('src/views/Transfer/transfer-mobile-application')),
    reducers: transferReducers,
    initialState: {
      collegeProfile: {
        userInfo: {
          user: EducationPhase.COMMUNITY_COLLEGE,
        },
        tab: 0,
        loading: true,
        modalPending: {},
      },
      transfer: true,
    },
  },
  [UserIdentifier.UG_STUDENT]: {
    app: lazy(() => import('src/views/Undergraduate')),
    mobileApp: lazy(() => import('src/views/Undergraduate/undergraduate-mobile-application')),
    reducers: undergraduateReducers,
    initialState: {
      collegeProfile: {
        userInfo: {
          user: EducationPhase.UNDERGRADUATE,
        },
        tab: 0,
        loading: true,
        modalPending: {},
      },
    },
  },
  [UserIdentifier.ADULT_LEARNER]: {
    app: lazy(() => import('src/views/AdultLearner')),
    mobileApp: lazy(() => import('src/views/AdultLearner/adult-learner-mobile-application')),
    reducers: undergraduateReducers,
    initialState: {
      collegeProfile: {
        userInfo: {
          user: EducationPhase.ADULT_LEARNER,
        },
        tab: 0,
        loading: true,
        modalPending: {},
      },
    },
  },
  [UserIdentifier.ADMIN]: {
    app: lazy(() => import('src/views/Admin')),
    reducers: adminReducers,
    initialState: {},
  },
  [UserIdentifier.PARENT]: {
    app: lazy(() => import('src/views/Parent/index')),
    mobileApp: lazy(() => import('src/views/Parent/parent-mobile-application')),
    reducers: parentReducers,
    initialState: {parent: {}},
  },
  [UserIdentifier.CP_USER]: {
    app: lazy(() => import('src/views/college-portal/index')),
    reducers: collegePortalReducers,
    initialState: collegePortalInitialState,
    modalConfigs: cpModals,
  },
  [UserIdentifier.DISTRICT_ADMIN]: {
    app: lazy(() => import('src/views/DistrictAdmin')),
    reducers: districtAdminReducers,
    initialState: {user: {type: 'DistrictAdmin'}},
  },
  [UserIdentifier.TEACHER]: {
    app: lazy(() => import('src/views/Educator')),
    mobileApp: lazy(() => import('src/views/Educator/educator-mobile-application')),
    reducers: educatorReducers,
    initialState: educatorInitialState,
  },
  [UserIdentifier.COUNSELOR]: {
    app: lazy(() => import('src/views/Educator')),
    mobileApp: lazy(() => import('src/views/Educator/educator-mobile-application')),
    reducers: educatorReducers,
    initialState: educatorInitialState,
  },
  [UserIdentifier.MENTOR]: {
    app: lazy(() => import('src/views/Educator')),
    mobileApp: lazy(() => import('src/views/Educator/educator-mobile-application')),
    reducers: educatorReducers,
    initialState: educatorInitialState,
  },
  [UserIdentifier.CC_STAFF]: {
    app: lazy(() => import('src/views/Educator')),
    mobileApp: lazy(() => import('src/views/Educator/educator-mobile-application')),
    reducers: educatorReducers,
    initialState: educatorInitialState,
  },
};

export const onboardingApps: ObjectOf<AppDeclaration> = {
  [UserIdentifier.HS_STUDENT]: {
    app: lazy(() => import('src/views/HighSchool/Onboarding')),
    mobileApp: lazy(() => import('src/views/HighSchool/Onboarding/high-school-mobile-onboarding')),
    reducers: onboardingReducers,
    initialState: {},
  },
  [UserIdentifier.CC_STUDENT]: {
    app: lazy(() => import('src/views/Transfer/onboarding')),
    mobileApp: lazy(() => import('src/views/Transfer/onboarding/transfer-mobile-onboarding')),
    reducers: onboardingReducers,
    initialState: {},
  },

  [UserIdentifier.PARENT]: {
    app: lazy(() => import('src/views/Parent/onboarding')),
    mobileApp: lazy(() => import('src/views/Parent/onboarding/parent-mobile-onboarding')),
    reducers: parentReducers,
    initialState: {},
  },
  [UserIdentifier.ADULT_LEARNER]: {
    app: lazy(() => import('src/views/AdultLearner/Onboarding')),
    mobileApp: lazy(
      () => import('src/views/AdultLearner/Onboarding/adult-leaner-mobile-onboarding')
    ),
    reducers: onboardingReducers,
    initialState: {},
  },
  [UserIdentifier.TEACHER]: {
    app: lazy(() => import('src/views/Educator/onboarding')),
    mobileApp: lazy(() => import('src/views/Educator/onboarding/educator-mobile-onboarding')),
    reducers: onboardingReducers,
    initialState: {educator: {}},
  },
  [UserIdentifier.COUNSELOR]: {
    app: lazy(() => import('src/views/Educator/onboarding')),
    mobileApp: lazy(() => import('src/views/Educator/onboarding/educator-mobile-onboarding')),
    reducers: onboardingReducers,
    initialState: {educator: {}},
  },
  [UserIdentifier.MENTOR]: {
    app: lazy(() => import('src/views/Educator/onboarding')),
    mobileApp: lazy(() => import('src/views/Educator/onboarding/educator-mobile-onboarding')),
    reducers: onboardingReducers,
    initialState: {educator: {}},
  },
  [UserIdentifier.CC_STAFF]: {
    app: lazy(() => import('src/views/Educator/onboarding')),
    mobileApp: lazy(() => import('src/views/Educator/onboarding/educator-mobile-onboarding')),
    reducers: onboardingReducers,
    initialState: {educator: {}},
  },
  [UserIdentifier.CP_USER]: {
    app: lazy(() => import('src/views/college-portal/ResetPassword')),
    reducers: collegePortalReducers,
    initialState: collegePortalInitialState,
  },
};
